/**
 * URL Builder Utility
 *
 * Utilities for constructing URLs with path parameters, query parameters,
 * and hash fragments. Handles type coercion, null/undefined filtering,
 * and special character encoding.
 *
 * @module shared/utils/routing/urlBuilder
 */

import type { RouteParams, QueryParams } from '@shared/types/routing';

/**
 * Build a complete URL from route pattern, path parameters, and query parameters
 *
 * Features:
 * - Replaces path parameters (`:id` → actual ID)
 * - Appends query string with proper encoding
 * - Handles hash fragments
 * - Filters out null/undefined values
 * - Type coercion for numbers/booleans
 *
 * @param route - Route pattern with parameter placeholders (e.g., '/organizations/:id')
 * @param params - Path parameters to replace placeholders
 * @param query - Query parameters to append as query string
 * @param hash - Optional hash fragment (without '#' prefix)
 * @returns Complete URL string
 *
 * @example
 * ```typescript
 * buildUrl('/organizations/:id', { id: 123 })
 * // Returns: '/organizations/123'
 *
 * buildUrl('/campaigns', undefined, { page: 2, limit: 20 })
 * // Returns: '/campaigns?page=2&limit=20'
 *
 * buildUrl('/routes/:routeId/stops', { routeId: 'abc' }, { expanded: true }, 'map')
 * // Returns: '/routes/abc/stops?expanded=true#map'
 * ```
 */
export function buildUrl(
  route: string,
  params?: RouteParams,
  query?: QueryParams,
  hash?: string
): string {
  let url = route;

  // Replace path parameters
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      const placeholder = `:${key}`;
      url = url.replace(placeholder, encodeURIComponent(String(value)));
    }
  }

  // Append query string
  if (query) {
    const queryString = buildQueryString(query);
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  // Append hash fragment
  if (hash) {
    url += `#${hash}`;
  }

  return url;
}

/**
 * Build a query string from QueryParams object
 *
 * Features:
 * - Filters out null/undefined values
 * - Handles arrays (multi-value params)
 * - Encodes special characters
 * - Converts booleans to 'true'/'false' strings
 * - Converts numbers to strings
 *
 * @param params - Query parameters object
 * @returns URL-encoded query string (without '?' prefix)
 *
 * @example
 * ```typescript
 * buildQueryString({ page: 2, limit: 20 })
 * // Returns: 'page=2&limit=20'
 *
 * buildQueryString({ tags: ['sales', 'urgent'], search: 'acme' })
 * // Returns: 'tags=sales&tags=urgent&search=acme'
 *
 * buildQueryString({ status: 'active', archived: null })
 * // Returns: 'status=active' (null filtered out)
 * ```
 */
export function buildQueryString(params: QueryParams): string {
  const entries: string[] = [];

  for (const [key, value] of Object.entries(params)) {
    // Skip null/undefined values
    if (value === null || value === undefined) {
      continue;
    }

    // Handle arrays (multi-value params)
    if (Array.isArray(value)) {
      for (const item of value) {
        entries.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
      }
      continue;
    }

    // Handle primitive values
    entries.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }

  return entries.join('&');
}

/**
 * Parse a query string into a QueryParams object
 *
 * Features:
 * - Decodes URL-encoded values
 * - Groups multi-value params into arrays
 * - Preserves original string values (no type coercion)
 *
 * @param search - Query string (with or without '?' prefix)
 * @returns Parsed QueryParams object
 *
 * @example
 * ```typescript
 * parseQueryString('?page=2&limit=20')
 * // Returns: { page: '2', limit: '20' }
 *
 * parseQueryString('tags=sales&tags=urgent&search=acme')
 * // Returns: { tags: ['sales', 'urgent'], search: 'acme' }
 * ```
 */
export function parseQueryString(search: string): QueryParams {
  const params: Record<string, string | string[]> = {};

  // Remove leading '?' if present
  const queryString = search.startsWith('?') ? search.slice(1) : search;

  if (!queryString) {
    return params;
  }

  for (const entry of queryString.split('&')) {
    const [key, value] = entry.split('=');
    if (!key) continue;

    const decodedKey = decodeURIComponent(key);
    const decodedValue = value ? decodeURIComponent(value) : '';

    // If key already exists, convert to array
    const existing = params[decodedKey];
    if (existing !== undefined) {
      if (Array.isArray(existing)) {
        existing.push(decodedValue);
      } else {
        params[decodedKey] = [existing, decodedValue];
      }
    } else {
      params[decodedKey] = decodedValue;
    }
  }

  return params;
}

/**
 * Append query parameters to an existing URL
 *
 * Preserves existing query parameters and merges with new ones.
 * If a parameter exists in both, the new value takes precedence.
 *
 * @param url - Existing URL (with or without query string)
 * @param params - Query parameters to append/merge
 * @returns Updated URL with merged query parameters
 *
 * @example
 * ```typescript
 * appendQueryParams('/organizations?page=1', { limit: 20 })
 * // Returns: '/organizations?page=1&limit=20'
 *
 * appendQueryParams('/campaigns?status=draft', { status: 'active' })
 * // Returns: '/campaigns?status=active' (new value overrides)
 * ```
 */
export function appendQueryParams(url: string, params: QueryParams): string {
  const [basePath, existingSearch, hash] = splitUrl(url);

  // Parse existing query parameters
  const existingParams = existingSearch ? parseQueryString(existingSearch) : {};

  // Merge with new parameters (new values override)
  const mergedParams = { ...existingParams, ...params };

  // Rebuild URL
  return buildUrl(basePath, undefined, mergedParams, hash);
}

/**
 * Remove query parameters from a URL
 *
 * @param url - URL to modify
 * @param keys - Parameter keys to remove
 * @returns URL with specified parameters removed
 *
 * @example
 * ```typescript
 * removeQueryParams('/campaigns?page=2&limit=20&status=active', ['page', 'limit'])
 * // Returns: '/campaigns?status=active'
 * ```
 */
export function removeQueryParams(url: string, keys: string[]): string {
  const [basePath, existingSearch, hash] = splitUrl(url);

  if (!existingSearch) {
    return url; // No query string to modify
  }

  const params = parseQueryString(existingSearch);

  // Remove specified keys
  for (const key of keys) {
    delete params[key];
  }

  return buildUrl(basePath, undefined, params, hash);
}

/**
 * Split a URL into base path, query string, and hash fragment
 *
 * @param url - URL to split
 * @returns [basePath, queryString, hash]
 *
 * @example
 * ```typescript
 * splitUrl('/campaigns?page=2#results')
 * // Returns: ['/campaigns', 'page=2', 'results']
 * ```
 */
function splitUrl(url: string): [string, string | undefined, string | undefined] {
  // Extract hash fragment
  const [pathAndQuery, hash] = url.split('#');

  // Extract query string
  const [basePath, queryString] = pathAndQuery.split('?');

  return [basePath, queryString, hash];
}

/**
 * Check if a URL matches a route pattern
 *
 * @param url - URL to test
 * @param pattern - Route pattern with parameter placeholders
 * @returns True if URL matches pattern
 *
 * @example
 * ```typescript
 * matchesRoute('/organizations/123', '/organizations/:id')
 * // Returns: true
 *
 * matchesRoute('/campaigns', '/organizations/:id')
 * // Returns: false
 * ```
 */
export function matchesRoute(url: string, pattern: string): boolean {
  const [urlPath] = splitUrl(url);
  const urlSegments = urlPath.split('/').filter(Boolean);
  const patternSegments = pattern.split('/').filter(Boolean);

  if (urlSegments.length !== patternSegments.length) {
    return false;
  }

  return patternSegments.every((segment, index) => {
    // Parameter placeholders match any value
    if (segment.startsWith(':')) {
      return true;
    }

    // Literal segments must match exactly
    return segment === urlSegments[index];
  });
}

/**
 * Extract path parameters from a URL based on a route pattern
 *
 * @param url - URL to extract from
 * @param pattern - Route pattern with parameter placeholders
 * @returns Extracted path parameters or null if URL doesn't match pattern
 *
 * @example
 * ```typescript
 * extractParams('/organizations/123', '/organizations/:id')
 * // Returns: { id: '123' }
 *
 * extractParams('/routes/abc/stops/xyz', '/routes/:routeId/stops/:stopId')
 * // Returns: { routeId: 'abc', stopId: 'xyz' }
 * ```
 */
export function extractParams(url: string, pattern: string): RouteParams | null {
  const [urlPath] = splitUrl(url);
  const urlSegments = urlPath.split('/').filter(Boolean);
  const patternSegments = pattern.split('/').filter(Boolean);

  if (urlSegments.length !== patternSegments.length) {
    return null;
  }

  const params: RouteParams = {};

  for (let i = 0; i < patternSegments.length; i++) {
    const segment = patternSegments[i];

    if (segment.startsWith(':')) {
      const paramName = segment.slice(1);
      params[paramName] = decodeURIComponent(urlSegments[i]);
    } else if (segment !== urlSegments[i]) {
      return null; // Literal segment doesn't match
    }
  }

  return params;
}
